import { useEffect, useState } from "react";
import api from "../lib/api";
import { Check, ChevronsUpDown, Loader2, Landmark } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "./ui/command";

export function BankCombobox({ currency, value, onSelect }) {
  const [open, setOpen] = useState(false);
  const [banks, setBanks] = useState([]);
  const [source, setSource] = useState("static");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get("/banks", { params: { currency } })
      .then((r) => {
        if (!active) return;
        setBanks(r.data.banks || []);
        setSource(r.data.source || "static");
      })
      .catch(() => active && setBanks([]))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [currency]);

  const selected = banks.find((b) => b.code === value);
  const label = selected ? `${selected.name} (${selected.code})` : value ? `Code ${value}` : "Select bank";

  return (
    <div className="space-y-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            data-testid="bank-combobox-trigger"
          >
            <span className="flex items-center gap-2 truncate">
              <Landmark className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{label}</span>
            </span>
            {loading ? <Loader2 className="ml-2 h-4 w-4 animate-spin opacity-60" /> : <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search bank or network..." data-testid="bank-combobox-search" />
            <CommandList>
              <CommandEmpty>No bank found.</CommandEmpty>
              <CommandGroup>
                {banks.map((b) => (
                  <CommandItem
                    key={b.code}
                    value={`${b.name} ${b.code}`}
                    onSelect={() => { onSelect(b.code); setOpen(false); }}
                    data-testid={`bank-option-${b.code}`}
                  >
                    <Check className={`mr-2 h-4 w-4 ${value === b.code ? "opacity-100" : "opacity-0"}`} />
                    <span className="flex-1 truncate">{b.name}</span>
                    <span className="ml-2 font-mono text-xs text-muted-foreground">{b.code}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <p className="text-[11px] text-muted-foreground" data-testid="bank-list-source">
        {source === "live" ? "Live list from your processor" : "Standard bank list"}
      </p>
    </div>
  );
}
